import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ShoppingBag, Phone, User } from "lucide-react";
import { Link } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { useCustomerAuth } from "../context/CustomerAuth";

export const Navbar = () => {
  const { count, setOpen } = useCart();
  const { user: customer, signIn } = useCustomerAuth();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const linkTone = scrolled
    ? "text-[#2D1B36]/70 hover:text-[#C5A059]"
    : "text-[#FBF9F6]/85 hover:text-[#DFCA9B]";
  const logoTone = scrolled ? "text-[#2D1B36]" : "text-[#FBF9F6]";
  const italicTone = scrolled ? "text-[#C5A059]" : "text-[#DFCA9B]";
  const subTone = scrolled ? "text-[#8A818F]" : "text-[#FBF9F6]/60";

  const links = [
    { label: "Collections", href: "#collections" },
    { label: "Shop", href: "#shop" },
    { label: "Our Story", href: "#story" },
  ];

  return (
    <motion.header
      data-testid="main-navbar"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, delay: 1.6, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled
          ? "bg-[#FBF9F6]/80 backdrop-blur-xl border-b border-[#E8E4DF]"
          : "bg-gradient-to-b from-[#2D1B36]/60 to-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-16 md:h-20 grid grid-cols-3 items-center">
        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <a
              key={l.label}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className={`font-accent text-xs tracking-[0.2em] uppercase ${linkTone} transition-colors duration-300`}
            >
              {l.label}
            </a>
          ))}
        </nav>
        <div className="md:hidden" />
        <a href="#top" data-testid="nav-logo" className="text-center">
          <span className={`font-serif-d text-xl md:text-2xl tracking-tight ${logoTone} transition-colors duration-300`}>
            Innus <span className={`italic ${italicTone} transition-colors duration-300`}>Lace Centre</span>
          </span>
          <span className={`block font-accent text-[9px] tracking-[0.35em] uppercase ${subTone} mt-0.5 transition-colors duration-300`}>
            Art &amp; Craft — N. Paravur
          </span>
        </a>
        <div className="flex items-center justify-end gap-5">
          <a
            href="tel:+917356572909"
            data-testid="nav-call-button"
            className={`hidden md:flex items-center gap-2 font-accent text-xs tracking-[0.15em] uppercase ${linkTone} transition-colors duration-300`}
          >
            <Phone size={13} strokeWidth={1.5} /> 73565 72909
          </a>
          {customer === undefined ? null : customer ? (
            <Link
              to="/account"
              data-testid="nav-account-link"
              className={`hidden sm:flex items-center gap-2 font-accent text-xs tracking-[0.15em] uppercase ${linkTone} transition-colors duration-300`}
            >
              {customer.picture ? (
                <img src={customer.picture} alt="" className="w-6 h-6 rounded-full object-cover" />
              ) : (
                <User size={14} strokeWidth={1.5} />
              )}
              <span className="max-w-[80px] truncate">{(customer.name || "Account").split(" ")[0]}</span>
            </Link>
          ) : (
            <Link
              to="/auth"
              data-testid="nav-signin-button"
              className={`hidden sm:block font-accent text-xs tracking-[0.15em] uppercase ${linkTone} transition-colors duration-300`}
            >
              Sign In
            </Link>
          )}
          <button
            onClick={() => setOpen(true)}
            data-testid="nav-cart-button"
            className="relative flex items-center gap-2 bg-[#4A2B58] text-[#FBF9F6] px-4 py-2 hover:bg-[#2D1B36] transition-colors duration-300"
          >
            <ShoppingBag size={15} strokeWidth={1.5} />
            <span className="font-accent text-xs tracking-[0.15em] uppercase hidden sm:inline">
              Cart
            </span>
            {count > 0 && (
              <span
                data-testid="nav-cart-count"
                className="absolute -top-2 -right-2 bg-[#C5A059] text-[#2D1B36] font-accent text-[10px] font-medium w-5 h-5 flex items-center justify-center rounded-full"
              >
                {count}
              </span>
            )}
          </button>
        </div>
      </div>
    </motion.header>
  );
};
